/**
 * Write rows to a real Excel file.
 *
 * The previous system's dashboard exports were XLSX, so this matches them. CSV
 * stays alongside rather than being replaced: it is what anything other than
 * Excel reads, and it is the format somebody feeds to a script.
 *
 * The library is loaded on DEMAND, not with the page. It is only needed by
 * somebody who actually clicks Excel, and adding it to the main bundle would make
 * every screen in the app pay for a feature used on four of them.
 *
 * SheetJS is deliberately not used: the copy on npm is stale at 0.18.5 with known
 * advisories, because the project moved distribution elsewhere. write-excel-file
 * is maintained, writes only, and is a fraction of the size.
 */

import type { CellObject, SheetData } from 'write-excel-file/browser';

export interface XlsxColumn {
  /** Header text. */
  header: string;
  /** Column width in characters. Excel's default is cramped for names and dates. */
  width?: number;
  /** Right-align and treat as a number, so the column can be summed in Excel. */
  numeric?: boolean;
  /** Excel number format for a numeric column. Hours want '0.00'; a count of days does not. */
  format?: string;
}

/**
 * Download rows as .xlsx.
 *
 * A numeric column is written as a NUMBER, not text. This is the whole reason to
 * offer Excel over CSV: a column of hours you cannot sum without re-typing it is
 * a screenshot with extra steps.
 */
export async function downloadXlsx(
  filename: string,
  sheetName: string,
  columns: XlsxColumn[],
  rows: (string | number | null | undefined)[][],
): Promise<void> {
  const writeXlsxFile = (await import('write-excel-file/browser')).default;

  const header: CellObject[] = columns.map((c) => ({
    value: c.header,
    fontWeight: 'bold',
    align: c.numeric ? 'right' : 'left',
  }));

  const body: CellObject[][] = rows.map((row) =>
    columns.map((column, i): CellObject => {
      const cell = row[i];
      // An empty cell rather than the string "null", which is what a naive
      // String(cell) writes into every gap in an indented breakdown.
      if (cell === null || cell === undefined || cell === '') return {};
      if (column.numeric && typeof cell === 'number') {
        // Written as a NUMBER, not text. This is the whole reason to offer Excel:
        // a column of hours nobody can sum without re-typing it is a screenshot
        // with extra steps.
        return { type: Number, value: cell, align: 'right', format: column.format ?? '0.00' };
      }
      return { value: String(cell) };
    }),
  );

  const data: SheetData = [header, ...body];

  // The browser build hands back writers rather than taking a filename: toFile
  // triggers the download itself, so there is no object URL to remember to revoke.
  await writeXlsxFile(data, {
    columns: columns.map((c) => ({ width: c.width ?? 18 })),
    sheet: sheetName,
    // Freezes the header row, so scrolling a long list keeps the column names.
    stickyRowsCount: 1,
  }).toFile(filename);
}
